import { api } from './client'
import { authStore } from '../auth/store'
import { resolveDeviceIdentity } from '../device'

/** 서버 Prisma enum 그대로. 번역 계층을 두지 않는다 */
export type AttendanceType = 'CHECKIN' | 'CHECKOUT' | 'OUT' | 'RETURN' | 'ABSENCE'

export interface AttendanceRecord {
  id: string
  type: AttendanceType
  /** UTC naive 로 저장된 값. 화면에는 KST 로 그린다 (kstTimeStr) */
  timestamp: string
  status: string
  isManual?: boolean
  manualReason?: string | null
  manualNote?: string | null
  manualStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null
  rejectionReason?: string | null
  nfcSpot?: { name: string } | null
  /**
   * 이 기록을 정정한 수기 기록의 id. 값이 있으면 "정정됨" 으로 표시한다.
   * 원본은 절대 지우거나 숨기지 않는다 — 둘 다 보여주고 관계만 밝힌다.
   */
  supersededById?: string | null
  /**
   * 그날 출근 없이 퇴근만 있을 때(야간조), 그 사람의 **바로 직전 기록**.
   *
   * 짝이라고 주장하지 않는다. 퇴근↔출근을 잇는 데이터가 없어서 추측하면
   * 간격이 전날일 수도 그저께일 수도 있다 — "전일" 이라 쓰면 거짓이 된다.
   * 사실만 보여주고 판단은 보는 사람이 한다.
   */
  prevTag?: { type: AttendanceType; timestamp: string } | null
}

export interface NfcCheckinInput {
  nfcUid: string
  requestType: 'CHECKIN' | 'CHECKOUT'
  nfcSignature?: string
  /** 멱등 키 — 오프라인 큐가 재전송해도 중복 INSERT 가 안 생긴다 */
  clientId: string
  /** 태그를 실제로 찍은 시각. 나중에 전송돼도 이 시각이 기록된다 */
  clientTimestamp: string
}

/**
 * NFC 태깅.
 *
 * deviceId 는 여기서 붙인다 — 호출부가 잊어버릴 수 없게.
 * 기기를 확인하지 못했으면 **요청 자체를 보내지 않는다** (스펙 §5-4).
 * 서버도 deviceId 미동봉 요청을 403 으로 막지만, 애초에 보내지 않는 게 맞다.
 */
export async function nfcCheckin(input: NfcCheckinInput) {
  const identity = await resolveDeviceIdentity()
  if (identity.source === 'unavailable') {
    return {
      success: false as const,
      error: '기기를 확인하지 못했습니다. 앱을 다시 열어 주세요.',
      status: 0, // 일시 실패로 취급 — 큐에 남겨 다음 기회에 재시도
    }
  }

  return api<AttendanceRecord>('/api/attendance/nfc-checkin', {
    method: 'POST',
    body: JSON.stringify({ ...input, deviceId: identity.id }),
  })
}

/** 오늘 내 기록 */
export function getTodayAttendance() {
  return api<AttendanceRecord[]>('/api/attendance/today')
}

/**
 * 기간 조회 — YYYY-MM-DD.
 *
 * `/api/attendance/my` 같은 건 없다. 목록 라우트는 `GET /api/attendance` 하나다.
 * 파라미터 이름도 from/to 다. startDate/endDate 가 아니다.
 *
 * 🔴 **`employeeId` 를 반드시 보낸다** (2026-09-15).
 *
 *   전에는 «EMPLOYEE 역할이면 서버가 알아서 본인으로 좁힌다» 에 기댔다.
 *   그 전제는 **반만 맞았다** — `attendance.controller.ts:42~44` 가 이렇다:
 *
 *     role === 'EMPLOYEE' ? req.user.userId : req.query.employeeId
 *
 *   관리자·부서장이 「내 기록」을 열면 `employeeId` 가 `undefined` 가 되어
 *   **좁히는 조건이 통째로 사라지고 조직 전체(부서장은 담당 부서 전체)가 나왔다.**
 *   조직을 넘지는 않지만(RLS·scopeEmployeeIds) **화면이 거짓말을 했다** —
 *   「내 기록」이라 써 놓고 남의 태깅을 보여 줬다. prod medifun 에서 실제로 봤다.
 *
 *   ⚠ 서버를 안 고치는 이유: `GET /api/attendance` 는 **DCMC 와 공유하는 경로**다.
 *     여기서 좁히면 관리자가 남의 기록을 보는 기존 동작(`/admin/attendance`)이 깨진다.
 *   ⚠ 그래서 **「안 보내면 전체가 나온다」는 성질은 서버에 그대로 남는다.**
 *     이 API 를 새로 부르는 곳을 만들 때마다 같은 자리를 밟는다.
 */
export function getMyAttendance(params: { from?: string; to?: string; take?: number; skip?: number }) {
  const q = new URLSearchParams()
  // 로그인한 본인. 역할과 무관하게 언제나 본인 것만 나오게 한다.
  const me = authStore.get().user?.userId
  if (me) q.set('employeeId', me)
  /*
   * 날짜만 보내면 안 된다. 서버는 `new Date('2026-08-21')` 로 파싱해서 **UTC 자정**으로 본다.
   * 그러면 to=오늘 은 "오늘 KST 09:00" 까지가 되어 그 뒤에 찍은 기록이 통째로 빠진다
   * (2026-08-21 확인: 09:05 KST 기록이 안 나옴).
   * Svelte 어드민은 예전부터 KST 오프셋을 붙여 보내고 있었다. 같은 형식으로 맞춘다.
   */
  if (params.from) q.set('from', `${params.from}T00:00:00+09:00`)
  if (params.to) q.set('to', `${params.to}T23:59:59.999+09:00`)
  if (params.take) q.set('take', String(params.take))
  if (params.skip) q.set('skip', String(params.skip))
  // 야간조 고아 퇴근에 "직전 기록" 을 붙여 달라는 표시. **React 만 보낸다** —
  // 같은 API 를 DCMC(Svelte)도 쓰는데 그쪽은 동결 대상이고, 켜면 쿼리가 더 나간다.
  q.set('withPrevTag', '1')
  return api<AttendanceRecord[]>(`/api/attendance?${q.toString()}`)
}

// ── 수기·부재 등록 ────────────────────────────────────────────

/** 출퇴근 수기 사유 — 태깅을 못 한 이유 */
export const MANUAL_REASONS = ['NFC_ERROR', 'NO_CARD', 'BROKEN_DEVICE', 'APP_ERROR', 'ETC'] as const
/** 부재 사유 — 애초에 출근하지 않은 날 */
export const ABSENCE_REASONS = ['VACATION', 'BUSINESS_TRIP', 'EDUCATION', 'OTHER'] as const

export interface ManualInput {
  type: 'CHECKIN' | 'CHECKOUT'
  /** KST YYYY-MM-DD */
  date: string
  /** KST HH:mm — 부재는 종일이라 보내지 않는다 */
  time?: string
  reason: string
  note?: string
  /**
   * 같은 날 부재가 이미 있어도 사용자가 확인했으면 true.
   * 서버는 하드 차단하지 않고 409 + code=ABSENCE_SAME_DAY 로 되묻는다.
   */
  force?: boolean
  /**
   * 기존 기록을 정정하는 경우 그 기록의 id.
   * 없으면 "새로 추가". 원본은 남고 정정본과 링크만 걸린다.
   */
  correctsId?: string
}

/**
 * 수기·부재 등록.
 *
 * 부재도 type 은 CHECKIN 으로 보낸다 — 서버가 사유를 보고 ABSENCE 로 매핑한다
 * (attendance.controller.ts, 2026-06-11). 클라가 판단하지 않는다.
 */
export function submitManualInput(input: ManualInput) {
  return api<AttendanceRecord>('/api/attendance/manual-input', {
    method: 'POST',
    body: JSON.stringify({ ...input, clientId: crypto.randomUUID() }),
  })
}

/**
 * 본인 이번 달 요약.
 *
 * workDays = 출근 태깅이 있는 KST 일자의 수. 표준 엑셀·통계와 같은 규칙이다.
 * 응답에 lateCount 도 있지만 **쓰지 않는다** — 판정을 필드로 들고 다니면
 * 화면마다 번진다. 지각 표시는 AttendanceTime 안에서만 계산한다.
 */
export function getMyMonthlyStats() {
  return api<{
    workDays: number
    /**
     * 휴가 잔여 — 회사가 넣은 부여 일수에서 쓴 만큼 뺀 것. 그게 전부다.
     * 발생 일수를 계산하지 않는다 (§1 경계). 음수도 그대로 온다 —
     * 선사용·특별휴가로 초과하는 건 정상 운영이고, 0 으로 감추면 사실과 다르다.
     */
    leaveTotalDays: number
    leaveUsedDays: number
    leaveRemainingDays: number
  }>('/api/attendance/my-monthly-stats')
}
